import type { IconProps } from "@tabler/icons-react";
import type { ComponentType } from "react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui";

/**
 * Fila de acceso rápido (diseño de referencia) · icono + título + descripción a
 * la izquierda y un botón naranja a la derecha (único elemento interactivo · no
 * se anida botón dentro de botón). `accent` = acción primaria (chip naranja
 * sólido); las secundarias usan chip ámbar tenue.
 */
export function QuickAction({
  icon: Icon,
  title,
  description,
  onClick,
  accent,
  cta,
  ariaLabel,
  className,
}: {
  icon: ComponentType<IconProps>;
  title: string;
  description: string;
  onClick: () => void;
  accent?: boolean;
  cta?: string;
  /** Nombre accesible del botón · desambigua cuando varios comparten el mismo `cta` visible. */
  ariaLabel?: string;
  className?: string;
}) {
  return (
    // Toda la fila es clickeable (el hover ya sugería que lo era) · `cursor-pointer`
    // en el mouse. El `<Button>` sigue siendo el control accesible/teclado; el
    // onClick del contenedor es solo el hit-target grande para el mouse (sin
    // role de botón para no anidar controles interactivos). El botón corta la
    // propagación para no navegar dos veces.
    <div
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 shadow-2xs transition-colors hover:bg-muted/40",
        className,
      )}
    >
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-lg",
            accent
              ? "bg-multiva-600 text-white shadow-2xs"
              : "border border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-300",
          )}
        >
          <Icon size={18} stroke={1.8} />
        </div>
        <div className="min-w-0">
          <h4 className="truncate text-xs font-bold text-foreground">{title}</h4>
          <p className="truncate text-[11px] text-muted-foreground">{description}</p>
        </div>
      </div>
      <Button
        variant="filled"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="shrink-0"
        aria-label={ariaLabel ?? cta ?? title}
      >
        {cta ?? title}
      </Button>
    </div>
  );
}
